// Theme mechanism (A13·W4 parity for the license admin-dashboard).
// Sets data-theme="light" | "dark" on <html>; the Polestead DS theme.css
// re-themes every surface off that attribute, and the shadcn dark bridge
// in index.css dark-flips the Radix component triplets.
//
// Precedence: explicit user choice in localStorage['ps-theme'] wins;
// otherwise fall back to the OS prefers-color-scheme. The no-FOUC inline
// script in index.html applies the same logic pre-paint — keep the two
// in sync (storage key + attribute name).

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'ps-theme';

/** The theme currently applied to <html>, or the resolved default. */
export function getTheme(): Theme {
  const el = document.documentElement;
  const attr = el.getAttribute('data-theme');
  if (attr === 'light' || attr === 'dark') return attr;
  return resolveInitialTheme();
}

/** Resolve the theme with no attribute set: stored choice, else OS pref. */
export function resolveInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* localStorage may be unavailable (private mode) — fall through */
  }
  const prefersDark =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
}

/** Apply a theme to <html> and persist the explicit choice. */
export function setTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore persistence failures */
  }
}

/** Flip between light and dark; returns the new theme. */
export function toggleTheme(): Theme {
  const next: Theme = getTheme() === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}

/** Ensure <html> carries a data-theme attribute (idempotent; the inline
 *  no-FOUC script normally does this first). Call once at app boot. */
export function initTheme(): Theme {
  const theme = resolveInitialTheme();
  const el = document.documentElement;
  if (el.getAttribute('data-theme') !== theme) {
    el.setAttribute('data-theme', theme);
  }
  return theme;
}
